import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Headphones, GraduationCap, X } from 'lucide-react';
import logger from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL = 15000; // 15 seconds

// Layer-2 defensive belt: persist banner dismissals across sign-in / reload.
// Stores only session_ids — no PII, no portfolio data. Bounded to the most
// recent 50 entries so the list can't grow unbounded over the user's lifetime.
// Layer-1 (POST /api/assisted-onboarding/sessions/{id}/leave) is the *real*
// fix that flips active → completed; this storage protects against any case
// where Layer-1 doesn't run (network failure mid-end, admin forgets, etc.).
const DISMISSED_STORAGE_KEY = 'mif:dismissedSessions';
const DISMISSED_CAP = 50;

const readDismissedSet = () => {
  try {
    const raw = localStorage.getItem(DISMISSED_STORAGE_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr : []);
  } catch { return new Set(); }
};

const persistDismissed = (set) => {
  try {
    const arr = Array.from(set).slice(-DISMISSED_CAP);
    localStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(arr));
  } catch { /* private mode / quota — silently degrade */ }
};

/**
 * Polls for active onboarding sessions and shows a persistent banner
 * prompting the user to join when a session is started by admin.
 */
export default function SessionNotificationBanner({ onJoinSession }) {
  const [activeSession, setActiveSession] = useState(null);
  const dismissedRef = useRef(readDismissedSet());
  const knownActiveRef = useRef(new Set());

  const token = localStorage.getItem('auth_token');

  const checkSessions = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/assisted-onboarding/sessions`, {
        headers: { 'Authorization': `Bearer ${token}` },
        cache: 'no-store',
      });
      if (!res.ok) return;
      const sessions = await res.json();
      const active = sessions.find(
        (s) => s.status === 'active' && !dismissedRef.current.has(s.session_id)
      );

      if (active && !knownActiveRef.current.has(active.session_id)) {
        knownActiveRef.current.add(active.session_id);
        setActiveSession(active);
      } else if (!active) {
        setActiveSession(null);
      }
    } catch (e) {
      logger.error('Session check failed:', e);
    }
  }, [token]);

  useEffect(() => {
    checkSessions();
    const interval = setInterval(checkSessions, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [checkSessions]);

  if (!activeSession) return null;

  const isQuick = activeSession.session_type === 'quick_setup';
  const Icon = isQuick ? Headphones : GraduationCap;
  const label = isQuick ? 'Quick Virtual Setup Session' : 'Deep Onboarding';
  const bg = isQuick ? 'from-blue-600 to-blue-700' : 'from-emerald-600 to-emerald-700';

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-60 bg-linear-to-r ${bg} text-white shadow-lg animate-in slide-in-from-top-2 duration-500`}
      data-testid="session-notification-banner"
    >
      <div className="container mx-auto px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between gap-2 sm:gap-4">
        <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-white/20 flex items-center justify-center animate-pulse shrink-0">
            <Headphones className="w-4 h-4 sm:w-5 sm:h-5" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-xs sm:text-sm truncate">
              Your {label} is live!
            </p>
            <p className="text-xs text-white/80 hidden sm:block">
              A MyInvestFolio specialist has started your session. Join now to begin.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            className="bg-white text-slate-900 hover:bg-white/90 font-semibold shadow-xs"
            onClick={() => onJoinSession?.(activeSession)}
            data-testid="session-join-now-btn"
          >
            <Icon className="w-4 h-4 mr-1.5" />
            Join Now
          </Button>
          <button
            onClick={() => {
              dismissedRef.current.add(activeSession.session_id);
              persistDismissed(dismissedRef.current);
              setActiveSession(null);
            }}
            className="text-white/60 hover:text-white p-1"
            data-testid="session-notification-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
